// [Codex 2026-07-16] S139 owns ordinary chat through Fintheon's durable run boundary.
/**
 * useFintheonChat Hook
 * Governed interactive chat with persisted Fintheon run recovery.
 */

// [claude-code 2026-04-18] Clear a stale cached conversation ID after a missing-session response.
// [claude-code 2026-03-09] Added surface-aware conversation history hydration on remount.
import { useCallback, useEffect, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import type { UIMessage } from "ai";
import { API_BASE_URL } from "../constants.js";
import { getAccessToken } from "../../../lib/supabase";
import { emitApiError } from "../../../lib/errorBus";
import {
  AI_CREDITS_EXHAUSTED,
  isAiCreditsExhausted,
} from "../../../lib/aiCreditErrors";
import type { ReasoningLevel } from "../reasoning";
import { readInteractiveChatSelection } from "../provider-options";
import type { AgenticTaskMode } from "../../../lib/agentic/task-mode";
import { publishCognitionStep } from "./chatUiActionHelpers";
import { readAttachedConnectorIds } from "../../../lib/chat-connector-attachments";
import { readLiveArbitrumRun } from "../../../lib/arbitrum-live-run";
import { readSurfaceRuntimeDescriptor } from "../../../lib/surface-capabilities";
import {
  buildFrontendDecisionContext,
  readDecisionUserContext,
} from "../../../lib/decision-context";
import {
  grokRuntimeErrorMessage,
  readGrokRuntimeReceipt,
  verifyGrokRuntimeSelection,
} from "../../../lib/grok-runtime-contract";
import { useSettings } from "../../../contexts/SettingsContext";
import {
  finishBackgroundTask,
  startBackgroundTask,
} from "../../../lib/background-task-notifications";
import { deriveConversationTitleFromInitialRequest } from "../session-title";
import { reduceAssistantUiEvent } from "../chat-ui-event-reducer";
import { useDurableRunRecovery } from "./useDurableRunRecovery";
import { CHAT_QUEUE_SNAPSHOT_EVENT } from "./useMessageQueue";

const REMOTE_CLOUD_CHAT_API_BASE = "https://fintheon.fly.dev";
const AI_ACTION_EVENT = "fintheon:ai-action-started";
const NARRATIVEFLOW_ALL_NARRATIVES_ID = "__all_narratives__";
const CHAT_BACKEND_UNREACHABLE = "Cannot reach the cloud chat backend.";
const DURABLE_CHAT_RUN_PATH = "/api/chat/runs";
const ORDINARY_CHAT_USES_DURABLE_RUN = true;

export interface FintheonWorkspaceContext {
  id: string;
  deskId?: string | null;
  deskSlug?: string | null;
  title: string;
  type?: string;
  status?: string;
  color?: string;
  hasArtifacts?: boolean;
  surfaceId?: string;
}

export interface QueuedRunContext {
  referenceContext?: Record<string, unknown>;
  riskFlowContext?: string;
  images?: string[];
}

/** Convert backend ChatMessage -> UIMessage for useChat hydration */
function backendToUIMessage(msg: {
  id: string;
  role: string;
  content: string;
  createdAt?: string;
  metadata?: Record<string, unknown>;
  parts?: Array<Record<string, unknown>>;
}): UIMessage {
  const durableParts = backendMessageParts(msg.parts);
  const attachments = backendMessageAttachments(msg.metadata);
  return {
    id: msg.id,
    role: msg.role as "user" | "assistant" | "system",
    parts:
      durableParts.length > 0
        ? durableParts
        : [
            { type: "text" as const, text: msg.content },
            ...attachments.map((attachment) => ({
              type: "file" as const,
              url: attachment.url,
              mediaType: attachment.mediaType,
              filename: `Attachment ${attachment.id.slice(0, 8)}`,
            })),
          ],
  };
}

function backendMessageParts(
  parts: Array<Record<string, unknown>> | undefined,
): UIMessage["parts"] {
  if (!Array.isArray(parts)) return [];
  const normalized: UIMessage["parts"] = [];
  for (const part of parts) {
    if (part.type === "text" && typeof part.text === "string") {
      normalized.push({ type: "text", text: part.text });
      continue;
    }
    if (
      part.type === "file" &&
      typeof part.url === "string" &&
      typeof part.mediaType === "string"
    ) {
      const attachmentId =
        typeof part.attachmentId === "string" ? part.attachmentId : "file";
      normalized.push({
        type: "file",
        url: part.url,
        mediaType: part.mediaType,
        filename: `Attachment ${attachmentId.slice(0, 8)}`,
      });
    }
  }
  return normalized;
}

function backendMessageAttachments(
  metadata: Record<string, unknown> | undefined,
): Array<{ id: string; url: string; mediaType: string }> {
  const attachments = metadata?.attachments;
  if (!Array.isArray(attachments)) return [];
  return attachments.flatMap((value) => {
    if (!value || typeof value !== "object") return [];
    const item = value as Record<string, unknown>;
    return typeof item.id === "string" &&
      typeof item.url === "string" &&
      typeof item.mediaType === "string"
      ? [{ id: item.id, url: item.url, mediaType: item.mediaType }]
      : [];
  });
}

function makeClientMessageId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function extractMessageText(msg: UIMessage): string {
  return (msg.parts ?? [])
    .filter(
      (part: any) => part.type === "text" && typeof part.text === "string",
    )
    .map((part: any) => part.text)
    .join("");
}

function extractMessageImages(msg: UIMessage): string[] {
  return (msg.parts ?? [])
    .filter(
      (part: any) =>
        (part.type === "image" && typeof part.image === "string") ||
        (part.type === "file" &&
          typeof part.url === "string" &&
          part.mediaType?.startsWith("image/")),
    )
    .map((part: any) => (part.image ?? part.url) as string);
}

async function updateConversationTitle(
  conversationId: string,
  title: string,
): Promise<void> {
  const token = await getAccessToken();
  const headers = new Headers({ "Content-Type": "application/json" });
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const response = await fetch(
    `${API_BASE_URL}/api/ai/conversations/${encodeURIComponent(
      conversationId,
    )}`,
    {
      method: "PATCH",
      headers,
      body: JSON.stringify({ title }),
    },
  );

  if (!response.ok) {
    throw new Error(`Conversation title update failed (${response.status})`);
  }
}

function toUserMessage(message: any): UIMessage {
  const parts =
    Array.isArray(message?.parts) && message.parts.length > 0
      ? message.parts
      : message?.text
        ? [{ type: "text" as const, text: message.text }]
        : [];
  return {
    id: message?.messageId ?? message?.id ?? makeClientMessageId("user"),
    role: "user",
    parts,
    metadata: message?.metadata,
  };
}

function readMessageAgenticMode(message: UIMessage | undefined) {
  const metadata = message?.metadata as
    | {
        custom?: { agenticMode?: unknown };
        unstable_state?: { agenticMode?: unknown };
        agenticMode?: unknown;
      }
    | undefined;
  const state = (
    message?.metadata as
      | { unstable_state?: { agenticMode?: unknown }; agenticMode?: unknown }
      | undefined
  )?.unstable_state;
  const value =
    metadata?.custom?.agenticMode ??
    state?.agenticMode ??
    metadata?.agenticMode;
  if (
    value === "plan" ||
    value === "discuss" ||
    value === "analyze" ||
    value === "draft"
  ) {
    return value;
  }
  return undefined;
}

function readMessageReferenceContext(
  message: UIMessage | undefined,
): Record<string, unknown> | undefined {
  const custom = (message?.metadata as { custom?: Record<string, unknown> })
    ?.custom;
  if (!custom) return undefined;
  const evidenceArtifactIds = Array.isArray(custom.evidenceArtifactIds)
    ? custom.evidenceArtifactIds
        .filter((value): value is string => typeof value === "string")
        .slice(0, 12)
    : [];
  const replyReferences = Array.isArray(custom.replyReferences)
    ? custom.replyReferences
        .filter((value): value is { messageId: string; text: string } =>
          Boolean(
            value &&
            typeof value === "object" &&
            typeof (value as any).messageId === "string" &&
            typeof (value as any).text === "string",
          ),
        )
        .slice(0, 6)
        .map((value) => ({
          messageId: value.messageId,
          text: value.text.slice(0, 280),
        }))
    : [];
  if (!evidenceArtifactIds.length && !replyReferences.length) return undefined;
  return { evidenceArtifactIds, replyReferences };
}

function readMessageRiskFlowContext(
  message: UIMessage | undefined,
): string | undefined {
  const value = (
    message?.metadata as { custom?: { riskFlowContext?: unknown } } | undefined
  )?.custom?.riskFlowContext;
  return typeof value === "string" && value.trim()
    ? value.trim().slice(0, 4_000)
    : undefined;
}

function publishAiActionStarted(endpoint: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(AI_ACTION_EVENT, {
      detail: { endpoint },
    }),
  );
}

function isAiRequestUrl(url: string): boolean {
  return /\/api\/(ai|harper|chat)\//.test(url);
}

function getCloudChatApiBase(): string {
  if (API_BASE_URL) return API_BASE_URL;
  if (typeof window === "undefined") return API_BASE_URL;
  if (!/^https?:$/.test(window.location.protocol)) return API_BASE_URL;
  if (/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) {
    return API_BASE_URL;
  }
  return REMOTE_CLOUD_CHAT_API_BASE;
}

function chatErrorMessageFromUnknown(error: unknown): string {
  const raw =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  const message = raw.trim();
  if (!message) return CHAT_BACKEND_UNREACHABLE;
  const lower = message.toLowerCase();
  if (
    lower.includes("failed to fetch") ||
    lower.includes("networkerror") ||
    lower.includes("load failed") ||
    lower.includes("response stream was empty") ||
    lower.includes("terminated") ||
    lower.includes("timeout")
  ) {
    return CHAT_BACKEND_UNREACHABLE;
  }
  return message;
}

function resolveFintheonChatEndpoint(): string {
  return `${getCloudChatApiBase()}${DURABLE_CHAT_RUN_PATH}`;
}

export function useFintheonChat(
  conversationId: string | undefined,
  setConversationId: (id: string) => void,
  agentOverride?: string,
  thinkHarder?: boolean,
  clearConversationId?: () => void,
  reasoningLevel?: ReasoningLevel,
  surfaceId?: string,
  workspaceContext?: FintheonWorkspaceContext | null,
  agenticMode?: AgenticTaskMode,
  connectorScopeId?: string | null,
) {
  const { chatPermissionLevel } = useSettings();
  const [isStreaming, setIsStreaming] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const lastErrorRef = useRef<string | null>(null);
  const rememberLastError = useCallback((message: string | null) => {
    lastErrorRef.current = message;
    setLastError(message);
  }, []);
  // [claude-code 2026-03-10] Track requestId from X-Request-Id header for cognition stream
  const [lastRequestId, setLastRequestId] = useState<string | null>(null);
  const [lastAgenticTaskId, setLastAgenticTaskId] = useState<string | null>(
    null,
  );
  const [pausedTaskId, setPausedTaskId] = useState<string | null>(null);
  const [pausingTaskId, setPausingTaskId] = useState<string | null>(null);
  const [recoveryRunId, setRecoveryRunId] = useState<string | null>(null);
  const lastAgenticTaskIdRef = useRef<string | null>(null);
  const activeRunBaseRef = useRef<string | null>(null);
  const [thinkingContent, setThinkingContent] = useState("");
  const hydratedRef = useRef<string | undefined>(undefined);
  const pendingConversationIdRef = useRef<string | null>(null);
  const pendingConversationTitleRef = useRef<string | null>(null);
  const renamedConversationIdsRef = useRef<Set<string>>(new Set());
  const runtimeChatIdRef = useRef<string | null>(null);
  const previousConversationIdRef = useRef(conversationId);
  // Abort controller ref — allows stop button to kill the active fetch
  const abortRef = useRef<AbortController | null>(null);
  const activeAgentTaskIdRef = useRef<string | null>(null);
  // [claude-code 2026-03-13] Ref to avoid stale closure in DefaultChatTransport's prepareSendMessagesRequest
  const thinkHarderRef = useRef(thinkHarder);
  const reasoningLevelRef = useRef<ReasoningLevel | undefined>(reasoningLevel);
  const surfaceIdRef = useRef(surfaceId);
  const workspaceContextRef = useRef(workspaceContext);
  const agenticModeRef = useRef<AgenticTaskMode | undefined>(agenticMode);
  const connectorScopeIdRef = useRef(connectorScopeId);
  const chatPermissionLevelRef = useRef(chatPermissionLevel);
  useEffect(() => {
    thinkHarderRef.current = thinkHarder;
    reasoningLevelRef.current = reasoningLevel;
    surfaceIdRef.current = surfaceId;
    workspaceContextRef.current = workspaceContext;
    agenticModeRef.current = agenticMode;
    connectorScopeIdRef.current = connectorScopeId;
    chatPermissionLevelRef.current = chatPermissionLevel;
  }, [
    thinkHarder,
    reasoningLevel,
    surfaceId,
    workspaceContext,
    agenticMode,
    connectorScopeId,
    chatPermissionLevel,
  ]);

  // [claude-code 2026-04-06] Ref for conversationId to avoid stale closures in transport callbacks
  const conversationIdRef = useRef(conversationId);
  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  const messagesRef = useRef<UIMessage[]>([]);

  const commitPendingConversationId = useCallback(() => {
    const pendingConversationId = pendingConversationIdRef.current;
    if (!pendingConversationId) return;
    const pendingConversationTitle = pendingConversationTitleRef.current;

    pendingConversationIdRef.current = null;
    pendingConversationTitleRef.current = null;
    hydratedRef.current = pendingConversationId;
    conversationIdRef.current = pendingConversationId;
    setConversationId(pendingConversationId);

    if (
      pendingConversationTitle &&
      !renamedConversationIdsRef.current.has(pendingConversationId)
    ) {
      renamedConversationIdsRef.current.add(pendingConversationId);
      void updateConversationTitle(
        pendingConversationId,
        pendingConversationTitle,
      ).catch((error) => {
        console.warn("[useFintheonChat] Could not rename conversation:", error);
      });
    }
  }, [setConversationId]);

  const beginAgentTask = useCallback((endpoint: string) => {
    if (activeAgentTaskIdRef.current) return;
    const surface = readCurrentSurface(surfaceIdRef.current);
    const source = surface === "narrativeflow" ? "narrativeflow" : "chat";
    const id = `agent-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    activeAgentTaskIdRef.current = id;
    startBackgroundTask({
      id,
      title: notificationTitleForSurface(source),
      detail: taskDetailForEndpoint(endpoint, source),
      source,
    });
  }, []);

  const finishAgentTask = useCallback(
    (status: "completed" | "failed", detail?: string) => {
      const id = activeAgentTaskIdRef.current;
      if (!id) return;
      activeAgentTaskIdRef.current = null;
      const surface = readCurrentSurface(surfaceIdRef.current);
      const source = surface === "narrativeflow" ? "narrativeflow" : "chat";
      finishBackgroundTask({
        id,
        title: notificationTitleForSurface(source),
        detail: notificationPreviewForTask(status, detail),
        source,
        status,
      });
    },
    [],
  );

  const fetchFn = useCallback(
    async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const url =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.toString()
            : input.url;
      const fullUrl = url.startsWith("http") ? url : `${API_BASE_URL}${url}`;
      const requestUrl = fullUrl;

      const headers = new Headers(init?.headers);
      headers.set("Content-Type", "application/json");

      // Attach Supabase JWT for backend auth
      const token = await getAccessToken();
      if (token) headers.set("Authorization", `Bearer ${token}`);

      // Use ref to get current conversationId (avoids stale closure in transport)
      const currentConvId = conversationIdRef.current;
      let body = init?.body;
      if (body && currentConvId) {
        try {
          const bodyObj = typeof body === "string" ? JSON.parse(body) : body;
          if (typeof bodyObj === "object" && bodyObj !== null) {
            bodyObj.conversationId = currentConvId;
            body = JSON.stringify(bodyObj);
          }
        } catch (e) {
          console.warn("[useFintheonChat] Could not inject conversationId:", e);
        }
      }

      // Durable runs can wait on tool approvals and resume from checkpoints.
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        if (isAiRequestUrl(requestUrl)) {
          publishAiActionStarted(requestUrl);
          beginAgentTask(requestUrl);
        }
        const response = await fetch(requestUrl, {
          ...init,
          headers,
          body,
          signal: controller.signal,
        });

        if (!response.ok) {
          let errText = grokRuntimeErrorMessage(response.status);
          try {
            const json = await response.clone().json();
            if (
              ![401, 403, 409, 429, 502, 503, 504].includes(response.status)
            ) {
              if (json?.details) errText = String(json.details);
              else if (json?.error) errText = String(json.error);
              else if (json?.message) errText = String(json.message);
            }
          } catch {
            /* response may not be JSON */
          }
          if (response.status === 402 || isAiCreditsExhausted(errText)) {
            emitApiError({
              code: AI_CREDITS_EXHAUSTED,
              message: "AI provider credits exhausted",
              status: 402,
              endpoint: DURABLE_CHAT_RUN_PATH,
            });
          }
          rememberLastError(errText);
          throw new Error(errText);
        }

        const runtimeReceipt = readGrokRuntimeReceipt(response.headers);
        if (!runtimeReceipt.ok) {
          rememberLastError(runtimeReceipt.message);
          throw new Error(runtimeReceipt.message);
        }
        const requestedSelection = routeSelectionFromBody(body);
        if (requestedSelection) {
          const routeReceipt = verifyGrokRuntimeSelection(
            runtimeReceipt.receipt,
            requestedSelection,
          );
          if (!routeReceipt.ok) {
            rememberLastError(routeReceipt.message);
            throw new Error(routeReceipt.message);
          }
        }

        rememberLastError(null);
        const {
          conversationId: convId,
          requestId: reqId,
          taskId,
        } = runtimeReceipt.receipt;
        if (convId && convId !== conversationIdRef.current) {
          pendingConversationIdRef.current = convId;
        }
        setLastRequestId(reqId);
        lastAgenticTaskIdRef.current = taskId;
        activeRunBaseRef.current = requestBase(requestUrl);
        setLastAgenticTaskId(taskId);

        return response;
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          // User-initiated stop — don't show error
          pendingConversationIdRef.current = null;
          pendingConversationTitleRef.current = null;
          setIsStreaming(false);
          throw error;
        }
        pendingConversationIdRef.current = null;
        pendingConversationTitleRef.current = null;
        if (
          !(error instanceof Error) ||
          !error.message.startsWith("Chat request failed")
        ) {
          rememberLastError(chatErrorMessageFromUnknown(error));
        }
        throw error;
      }
    },
    [agentOverride, beginAgentTask, rememberLastError],
  );

  // Every ordinary UI persona routes through the durable Fintheon-owned run boundary.
  const usesDurableRun = ORDINARY_CHAT_USES_DURABLE_RUN;
  const chatEndpoint = resolveFintheonChatEndpoint();
  if (!runtimeChatIdRef.current) {
    runtimeChatIdRef.current = `fintheon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
  const runtimeChatId = runtimeChatIdRef.current;

  const {
    messages: useChatMessages,
    sendMessage,
    status,
    setMessages: setUseChatMessages,
    stop,
    regenerate,
    resumeStream,
    addToolResult,
    addToolOutput,
    addToolApprovalResponse,
  } = useChat({
    id: runtimeChatId,
    transport: new DefaultChatTransport({
      api: chatEndpoint,
      fetch: fetchFn,
      prepareSendMessagesRequest: ({ messages }) => {
        const currentConversationId = conversationIdRef.current;
        const firstUserMessage = messages.find(
          (message) => message.role === "user",
        );
        pendingConversationTitleRef.current = currentConversationId
          ? null
          : deriveConversationTitleFromInitialRequest(
              firstUserMessage ? extractMessageText(firstUserMessage) : "",
            );

        const lastUserMsg = [...messages]
          .reverse()
          .find((message) => message.role === "user");
        const msgText = lastUserMsg ? extractMessageText(lastUserMsg) : "";
        const messageMode = readMessageAgenticMode(lastUserMsg);
        const referenceContext = readMessageReferenceContext(lastUserMsg);
        const queuedRiskFlowContext = readMessageRiskFlowContext(lastUserMsg);
        const images = lastUserMsg ? extractMessageImages(lastUserMsg) : [];
        const history = messages.slice(0, -1).map((message) => ({
          role: message.role as "user" | "assistant",
          content: extractMessageText(message),
        }));
        const currentSurface = readCurrentSurface(surfaceIdRef.current);
        const currentWorkspace = workspaceContextRef.current ?? undefined;
        const activeConnectors = readAttachedConnectorIds(
          connectorScopeIdRef.current,
        );
        const userContext = readDecisionUserContext();
        const decisionContext = buildFrontendDecisionContext({
          surfaceId: currentSurface,
          conversationId: conversationIdRef.current,
          connectorScopeId: connectorScopeIdRef.current,
          workspace: currentWorkspace,
          userContext,
          userMode: messageMode ?? agenticModeRef.current ?? "analyze",
          persona: agentOverride ?? "harper-cao",
        });
        const runtimeSelection = readInteractiveChatSelection();
        return {
          body: {
            message: msgText,
            ...(images.length > 0 && { images }),
            history,
            provider: runtimeSelection.provider,
            model: runtimeSelection.model,
            ...(conversationIdRef.current && {
              conversationId: conversationIdRef.current,
            }),
            ...(thinkHarderRef.current && { thinkHarder: true }),
            ...(reasoningLevelRef.current && {
              reasoningLevel: reasoningLevelRef.current,
            }),
            ...(agentOverride && { persona: agentOverride }),
            ...(queuedRiskFlowContext
              ? { riskFlowContext: queuedRiskFlowContext }
              : {}),
            mode: messageMode ?? agenticModeRef.current ?? "analyze",
            ...(currentWorkspace && { workspace: currentWorkspace }),
            metadata: buildChatMetadata(
              currentSurface,
              currentWorkspace,
              activeConnectors,
              referenceContext,
            ),
            userContext,
            decisionContext,
            activeConnectors,
            permissionLevel: chatPermissionLevelRef.current,
            surface: currentSurface,
          },
        };
      },
    }),
    onFinish: (result?: { message?: UIMessage; messages?: UIMessage[] }) => {
      setIsStreaming(false);
      commitPendingConversationId();
      finishAgentTask(
        "completed",
        result?.message
          ? normalizeNotificationPreview(extractMessageText(result.message))
          : result?.messages
            ? latestAssistantPreview(result.messages)
            : latestAssistantPreview(messagesRef.current),
      );
    },
    onError: (error?: unknown) => {
      pendingConversationIdRef.current = null;
      pendingConversationTitleRef.current = null;
      setIsStreaming(false);
      const message =
        lastErrorRef.current ?? chatErrorMessageFromUnknown(error);
      rememberLastError(message);
      finishAgentTask("failed", message);
    },
  });

  useEffect(() => {
    messagesRef.current = useChatMessages;
  }, [useChatMessages]);

  useDurableRunRecovery({
    enabled: usesDurableRun,
    baseUrl: getCloudChatApiBase(),
    conversationId,
    runId: recoveryRunId,
    onActive: (run) => {
      lastAgenticTaskIdRef.current = run.id;
      activeRunBaseRef.current = getCloudChatApiBase();
      setLastAgenticTaskId(run.id);
      const paused = run.status === "paused";
      setPausedTaskId(paused ? run.id : null);
      if (paused) setPausingTaskId(null);
      setIsStreaming(!paused && run.status === "running");
    },
    onUiEvent: (run, event) => {
      if (event.type === "data-run-event") {
        const runEvent = event.data as Record<string, unknown> | undefined;
        if (runEvent?.kind === "paused") {
          setPausingTaskId(null);
          setPausedTaskId(run.id);
          setIsStreaming(false);
        } else if (runEvent?.kind === "resumed") {
          setPausedTaskId(null);
          setPausingTaskId(null);
          setIsStreaming(true);
        }
        return;
      }
      if (!isRenderableUiEvent(event)) return;
      const messageId = `recovered-${run.id}`;
      setUseChatMessages((current) => {
        const existing = current.find((message) => message.id === messageId);
        const base: UIMessage = existing ?? {
          id: messageId,
          role: "assistant",
          parts: [],
        };
        const updated = reduceAssistantUiEvent(base, event);
        return existing
          ? current.map((message) =>
              message.id === messageId ? updated : message,
            )
          : [...current, updated];
      });
      if (event.type === "reasoning-delta" && typeof event.delta === "string") {
        setThinkingContent((current) => current + event.delta);
      }
    },
    onFinished: (run) => {
      setPausedTaskId(null);
      setPausingTaskId(null);
      setRecoveryRunId(null);
      setIsStreaming(false);
      if (run.status !== "completed" || !conversationId) return;
      void hydrateRecoveredConversation({
        conversationId,
        surfaceId: surfaceIdRef.current,
        workspaceContext: workspaceContextRef.current,
        setMessages: setUseChatMessages,
        onHydrated: () => {
          hydratedRef.current = conversationId;
          rememberLastError(null);
        },
      });
    },
    onRecoveryError: (_run, message) => {
      setIsStreaming(false);
      rememberLastError(message);
    },
    onQueueSnapshot: (recoveredConversationId, items) => {
      window.dispatchEvent(
        new CustomEvent(CHAT_QUEUE_SNAPSHOT_EVENT, {
          detail: { conversationId: recoveredConversationId, items },
        }),
      );
    },
  });

  const sendHarperMessage = useCallback(
    async (message: any) => {
      const userMessage = toUserMessage(message);
      const previousMessages = messagesRef.current;
      const requestMessages = [...previousMessages, userMessage];
      const assistantId = makeClientMessageId("assistant");
      const assistantMessage: UIMessage = {
        id: assistantId,
        role: "assistant",
        parts: [{ type: "text" as const, text: "" }],
      };

      rememberLastError(null);
      setPausedTaskId(null);
      setPausingTaskId(null);
      setRecoveryRunId(null);
      setThinkingContent("");
      setIsStreaming(true);
      setUseChatMessages([...requestMessages, assistantMessage]);

      const msgText = extractMessageText(userMessage);
      const images = extractMessageImages(userMessage);
      const history = previousMessages
        .filter((msg) => msg.role === "user" || msg.role === "assistant")
        .map((msg) => ({
          role: msg.role as "user" | "assistant",
          content: extractMessageText(msg),
        }));
      const currentSurface = readCurrentSurface(surfaceIdRef.current);
      const currentWorkspace = workspaceContextRef.current ?? undefined;
      const messageMode = readMessageAgenticMode(userMessage);
      const referenceContext = readMessageReferenceContext(userMessage);
      const activeConnectors = readAttachedConnectorIds(
        connectorScopeIdRef.current,
      );
      const userContext = readDecisionUserContext();
      const decisionContext = buildFrontendDecisionContext({
        surfaceId: currentSurface,
        conversationId: conversationIdRef.current,
        connectorScopeId: connectorScopeIdRef.current,
        workspace: currentWorkspace,
        userContext,
        userMode: messageMode ?? agenticModeRef.current ?? "analyze",
        persona: agentOverride ?? "harper-cao",
      });
      const runtimeSelection = readInteractiveChatSelection();

      let assistantText = "";
      try {
        const requestBody = JSON.stringify({
          message: msgText,
          ...(images.length > 0 && { images }),
          history,
          provider: runtimeSelection.provider,
          model: runtimeSelection.model,
          ...(conversationIdRef.current && {
            conversationId: conversationIdRef.current,
          }),
          ...(thinkHarderRef.current && { thinkHarder: true }),
          ...(reasoningLevelRef.current && {
            reasoningLevel: reasoningLevelRef.current,
          }),
          mode: messageMode ?? agenticModeRef.current ?? "analyze",
          ...(currentWorkspace && { workspace: currentWorkspace }),
          metadata: buildChatMetadata(
            currentSurface,
            currentWorkspace,
            activeConnectors,
            referenceContext,
          ),
          userContext,
          decisionContext,
          activeConnectors,
          permissionLevel: chatPermissionLevelRef.current,
          surface: currentSurface,
        });
        const endpoint = resolveFintheonChatEndpoint();
        const response = await fetchFn(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: requestBody,
        });

        if (!response.ok) {
          let errText = `Chat request failed (${response.status})`;
          try {
            const json = await response.clone().json();
            if (json?.details) errText = String(json.details);
            else if (json?.error) errText = String(json.error);
            else if (json?.message) errText = String(json.message);
          } catch {
            /* response may not be JSON */
          }
          rememberLastError(errText);
          throw new Error(errText);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error("CAO response stream was empty.");
        const decoder = new TextDecoder();
        let buffer = "";

        const applyUiEvent = (event: Record<string, unknown>) => {
          setUseChatMessages((current) =>
            current.map((msg) =>
              msg.id === assistantId ? reduceAssistantUiEvent(msg, event) : msg,
            ),
          );
        };

        const handleEvent = (raw: string) => {
          const dataLines = raw
            .split("\n")
            .filter((line) => line.startsWith("data:"))
            .map((line) => line.replace(/^data:\s?/, "").trim());
          for (const data of dataLines) {
            if (!data || data === "[DONE]") continue;
            const event = JSON.parse(data);
            if (
              event.type === "text-delta" &&
              typeof event.delta === "string"
            ) {
              assistantText += event.delta;
            }
            if (event.type === "reasoning-start") {
              setThinkingContent("");
            }
            if (
              event.type === "reasoning-delta" &&
              typeof event.delta === "string"
            ) {
              setThinkingContent((current) => current + event.delta);
            }
            if (
              event.type === "cognition-step" &&
              typeof event.requestId === "string" &&
              event.step
            ) {
              publishCognitionStep(event.requestId, event.step);
            }
            if (event.type === "error") {
              throw new Error(event.errorText || "CAO stream failed");
            }
            applyUiEvent(event);
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const frames = buffer.split("\n\n");
          buffer = frames.pop() ?? "";
          for (const frame of frames) {
            if (frame.startsWith(":")) continue;
            handleEvent(frame);
          }
        }
        buffer += decoder.decode();
        if (buffer.trim()) handleEvent(buffer);
        commitPendingConversationId();
        const completedConversationId = conversationIdRef.current;
        if (completedConversationId) {
          await hydrateRecoveredConversation({
            conversationId: completedConversationId,
            surfaceId: surfaceIdRef.current,
            workspaceContext: workspaceContextRef.current,
            setMessages: setUseChatMessages,
            onHydrated: () => {
              hydratedRef.current = completedConversationId;
              rememberLastError(null);
            },
          });
        }
        finishAgentTask("completed", assistantText);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          pendingConversationIdRef.current = null;
          pendingConversationTitleRef.current = null;
          return;
        }
        pendingConversationIdRef.current = null;
        pendingConversationTitleRef.current = null;
        const message = chatErrorMessageFromUnknown(error);
        rememberLastError(message);
        finishAgentTask("failed", message);
        throw error;
      } finally {
        abortRef.current = null;
        setIsStreaming(false);
      }
    },
    [
      agentOverride,
      commitPendingConversationId,
      fetchFn,
      finishAgentTask,
      rememberLastError,
      setUseChatMessages,
    ],
  );

  useEffect(() => {
    const previousConversationId = previousConversationIdRef.current;
    previousConversationIdRef.current = conversationId;

    if (conversationId || !previousConversationId) return;

    pendingConversationIdRef.current = null;
    pendingConversationTitleRef.current = null;
    hydratedRef.current = undefined;
    setUseChatMessages([]);
  }, [conversationId, setUseChatMessages]);

  // [claude-code 2026-04-10] Hydrate messages when conversationId changes (session switch or remount)
  useEffect(() => {
    console.debug("[useFintheonChat] Hydration effect fired", {
      conversationId,
      hydrated: hydratedRef.current,
    });
    if (!conversationId || hydratedRef.current === conversationId) {
      console.debug(
        "[useFintheonChat] Skipping hydration — already hydrated or no ID",
      );
      return;
    }

    let cancelled = false;

    // Clear existing messages so the new session loads fresh
    setUseChatMessages([]);
    // Reset hydrated ref so useChat with new id starts clean
    hydratedRef.current = undefined;

    (async () => {
      try {
        const token = await getAccessToken();
        const headers: Record<string, string> = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;
        const hydrationUrl = buildConversationHydrationUrl({
          conversationId,
          surfaceId: surfaceIdRef.current,
          workspaceContext: workspaceContextRef.current,
        });
        console.debug(
          "[useFintheonChat] Fetching conversation",
          conversationId,
        );
        const res = await fetch(hydrationUrl, { headers });
        if (cancelled) return;
        if (!res.ok) {
          console.warn(
            `[useFintheonChat] Conversation ${conversationId} not found (${res.status}) — starting fresh`,
          );
          // [claude-code 2026-04-18] Clear the stale cached ID so downstream consumers
          // (FintheonComposer relay dispatch, session list, hydration effect) don't keep
          // firing requests against a conversation that no longer exists. If the clear
          // function wasn't threaded, at least stamp hydratedRef so we don't loop.
          if (clearConversationId) {
            clearConversationId();
          } else {
            hydratedRef.current = conversationId;
          }
          return;
        }
        const data = await res.json();
        const msgs: UIMessage[] = (data.messages ?? [])
          .filter((m: any) => m.role === "user" || m.role === "assistant")
          .map(backendToUIMessage);
        console.debug(
          "[useFintheonChat] Hydrated",
          msgs.length,
          "messages for",
          conversationId,
        );
        if (!cancelled) {
          setUseChatMessages(msgs);
          hydratedRef.current = conversationId;
        }
      } catch (err) {
        console.error("[useFintheonChat] Conversation hydration failed:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stop requests a safe-checkpoint pause, then detaches this client stream.
  const hardStop = useCallback(() => {
    const taskId = lastAgenticTaskIdRef.current;
    const detach = () => {
      abortRef.current?.abort();
      abortRef.current = null;
      pendingConversationTitleRef.current = null;
      stop();
      setIsStreaming(false);
    };
    if (taskId) {
      commitPendingConversationId();
      setPausingTaskId(taskId);
      setRecoveryRunId(taskId);
      const base = activeRunBaseRef.current ?? getCloudChatApiBase();
      void controlChatRun(base, taskId, "pause")
        .then((result) => {
          if (result.run?.status === "paused") {
            setPausingTaskId(null);
            setPausedTaskId(taskId);
          } else if (
            result.run &&
            ["completed", "failed", "blocked", "cancelled"].includes(
              result.run.status,
            )
          ) {
            setPausingTaskId(null);
            setPausedTaskId(null);
          }
        })
        .catch((error) => {
          setPausingTaskId(null);
          setPausedTaskId(null);
          setRecoveryRunId(null);
          rememberLastError("Harper could not pause this run. Try again.");
          console.warn("[useFintheonChat] Could not persist pause:", error);
        })
        .finally(detach);
      return;
    }
    detach();
  }, [commitPendingConversationId, rememberLastError, stop]);

  const resumeActiveRun = useCallback(async () => {
    const taskId = pausedTaskId ?? lastAgenticTaskIdRef.current;
    if (!taskId) return;
    const base = activeRunBaseRef.current ?? getCloudChatApiBase();
    await controlChatRun(base, taskId, "resume");
    setPausedTaskId(null);
    setPausingTaskId(null);
    setRecoveryRunId(taskId);
    setIsStreaming(true);
    rememberLastError(null);
  }, [pausedTaskId, rememberLastError]);

  const steerActiveRun = useCallback(
    async (message: string, queueId?: string, context?: QueuedRunContext) => {
      const taskId = lastAgenticTaskIdRef.current;
      if (!taskId || !message.trim()) {
        throw new Error("There is no active chat run to steer.");
      }
      const base = activeRunBaseRef.current ?? getCloudChatApiBase();
      await controlChatRunMessage(
        base,
        taskId,
        "steer",
        message,
        queueId,
        context,
      );
    },
    [],
  );

  const queueActiveRun = useCallback(
    async (message: string, queueId?: string, context?: QueuedRunContext) => {
      const taskId = lastAgenticTaskIdRef.current;
      if (!taskId || !message.trim()) {
        throw new Error("There is no active chat run to queue against.");
      }
      const base = activeRunBaseRef.current ?? getCloudChatApiBase();
      await controlChatRunMessage(
        base,
        taskId,
        "queue",
        message,
        queueId,
        context,
      );
    },
    [],
  );

  const updateQueuedRunMessage = useCallback(
    async (queueId: string, message: string) => {
      const taskId = lastAgenticTaskIdRef.current;
      if (!taskId || !message.trim())
        throw new Error("Queued message is unavailable.");
      const base = activeRunBaseRef.current ?? getCloudChatApiBase();
      await controlChatRunMessage(base, taskId, "update", message, queueId);
    },
    [],
  );

  const removeQueuedRunMessage = useCallback(async (queueId: string) => {
    const taskId = lastAgenticTaskIdRef.current;
    if (!taskId) throw new Error("Queued message is unavailable.");
    const base = activeRunBaseRef.current ?? getCloudChatApiBase();
    await controlChatRunMessage(base, taskId, "remove", undefined, queueId);
  }, []);

  return {
    messages: useChatMessages,
    sendMessage: usesDurableRun ? sendHarperMessage : sendMessage,
    status,
    setMessages: setUseChatMessages,
    isLoading: isStreaming || status === "streaming" || status === "submitted",
    setIsStreaming,
    stop: hardStop,
    regenerate,
    resumeStream,
    addToolResult,
    addToolOutput,
    addToolApprovalResponse,
    lastError,
    clearError: () => rememberLastError(null),
    lastRequestId,
    lastAgenticTaskId,
    pausedTaskId,
    pausingTaskId,
    resumeActiveRun,
    queueActiveRun,
    steerActiveRun,
    updateQueuedRunMessage,
    removeQueuedRunMessage,
    thinkingContent,
  };
}

function routeSelectionFromBody(
  body: BodyInit | null | undefined,
): { provider: string; model?: string | null } | null {
  if (typeof body !== "string") return null;
  try {
    const parsed = JSON.parse(body) as Record<string, unknown>;
    if (typeof parsed.provider !== "string") return null;
    return {
      provider: parsed.provider,
      model: typeof parsed.model === "string" ? parsed.model : null,
    };
  } catch {
    return null;
  }
}

function requestBase(url: string): string {
  try {
    return new URL(url, window.location.origin).origin;
  } catch {
    return getCloudChatApiBase();
  }
}

function isRenderableUiEvent(event: Record<string, unknown>): boolean {
  const type = typeof event.type === "string" ? event.type : "";
  return (
    type.startsWith("text-") ||
    type.startsWith("reasoning-") ||
    type.startsWith("tool-")
  );
}

async function hydrateRecoveredConversation(input: {
  conversationId: string;
  surfaceId?: string;
  workspaceContext?: FintheonWorkspaceContext | null;
  setMessages: (messages: UIMessage[]) => void;
  onHydrated: () => void;
}) {
  const token = await getAccessToken();
  const headers = new Headers();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const response = await fetch(
    buildConversationHydrationUrl({
      conversationId: input.conversationId,
      surfaceId: input.surfaceId,
      workspaceContext: input.workspaceContext,
    }),
    { headers },
  );
  if (!response.ok) return;
  const data = await response.json();
  const messages = (data.messages ?? [])
    .filter((message: any) => ["user", "assistant"].includes(message.role))
    .map(backendToUIMessage);
  input.setMessages(messages);
  input.onHydrated();
}

async function controlChatRun(
  base: string,
  taskId: string,
  action: "pause" | "resume" | "cancel",
): Promise<{ run?: { id: string; status: string } }> {
  const token = await getAccessToken();
  const headers = new Headers({ "Content-Type": "application/json" });
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const response = await fetch(
    `${base}${DURABLE_CHAT_RUN_PATH}/${encodeURIComponent(taskId)}/${action}`,
    { method: "POST", headers },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(
      body?.error || `Run ${action} failed (${response.status}).`,
    );
  }
  return response.json().catch(() => ({}));
}

async function controlChatRunMessage(
  base: string,
  taskId: string,
  action: "queue" | "steer" | "update" | "remove",
  message?: string,
  queueId?: string,
  context?: QueuedRunContext,
): Promise<void> {
  const token = await getAccessToken();
  const headers = new Headers({ "Content-Type": "application/json" });
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const response = await fetch(
    `${base}${DURABLE_CHAT_RUN_PATH}/${encodeURIComponent(taskId)}/control`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({
        action,
        ...(message ? { message: message.trim() } : {}),
        ...(queueId ? { queueId } : {}),
        ...(context?.images?.length ? { images: context.images } : {}),
        ...(context?.referenceContext || context?.riskFlowContext
          ? {
              context: {
                ...(context.referenceContext
                  ? { referenceContext: context.referenceContext }
                  : {}),
                ...(context.riskFlowContext
                  ? { riskFlowContext: context.riskFlowContext }
                  : {}),
              },
            }
          : {}),
      }),
    },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(
      body?.error || `Run ${action} failed (${response.status}).`,
    );
  }
}

function readCurrentSurface(surfaceId?: string): string | undefined {
  if (surfaceId) return surfaceId.split(":")[0] || surfaceId;
  try {
    return localStorage.getItem("fintheon:current-surface") ?? undefined;
  } catch {
    return undefined;
  }
}

function notificationTitleForSurface(source?: string): string {
  return source === "narrativeflow" ? "NarrativeFlow" : "CAO Chat";
}

function latestAssistantPreview(messages: UIMessage[]): string | undefined {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message?.role !== "assistant") continue;
    const preview = normalizeNotificationPreview(extractMessageText(message));
    if (preview) return preview;
  }
  return undefined;
}

function notificationPreviewForTask(
  status: "completed" | "failed",
  detail?: string,
): string {
  const preview = normalizeNotificationPreview(detail);
  if (preview) return preview;
  return status === "failed"
    ? "The request did not complete."
    : "The response finished.";
}

function normalizeNotificationPreview(value?: string): string | undefined {
  const clean = value
    ?.replace(/```[\s\S]*?```/g, " ")
    .replace(/[#*_`>\[\]()]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!clean) return undefined;
  return clean.length <= 180 ? clean : `${clean.slice(0, 177).trimEnd()}...`;
}

function buildChatMetadata(
  surface: string | undefined,
  workspace: FintheonWorkspaceContext | undefined,
  activeConnectors: string[] = [],
  referenceContext?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const arbitrumRun = activeConnectors.includes("arbitrum")
    ? readLiveArbitrumRun()
    : null;
  const runtime = readSurfaceRuntimeDescriptor();
  if (!surface && !workspace && !arbitrumRun && !runtime && !referenceContext) {
    return undefined;
  }
  return {
    runtime,
    ...(surface ? { surface } : {}),
    ...(workspace ? { workspace } : {}),
    ...(arbitrumRun ? { arbitrumRun } : {}),
    ...(referenceContext ?? {}),
  };
}

function buildConversationHydrationUrl(input: {
  conversationId: string;
  surfaceId?: string;
  workspaceContext?: FintheonWorkspaceContext | null;
}): string {
  const surface = readCurrentSurface(input.surfaceId);
  const workspaceId = input.workspaceContext?.id;
  if (
    surface === "narrativeflow" &&
    workspaceId &&
    workspaceId !== NARRATIVEFLOW_ALL_NARRATIVES_ID
  ) {
    return `${API_BASE_URL}/api/narrative/sessions/${encodeURIComponent(
      workspaceId,
    )}/conversations/${encodeURIComponent(input.conversationId)}`;
  }

  return `${API_BASE_URL}/api/ai/conversations/${encodeURIComponent(
    input.conversationId,
  )}`;
}

function taskDetailForEndpoint(
  endpoint: string,
  source: "chat" | "narrativeflow",
) {
  if (source === "narrativeflow") {
    return "NarrativeFlow will keep working if you leave this tab.";
  }
  return endpoint.includes(DURABLE_CHAT_RUN_PATH)
    ? "Harper will keep working if you leave this tab."
    : "The agent will keep working if you leave this tab.";
}
